import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { HelpCircle, MessageCircle, Calendar, FileText, MapPin, Users, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

export function FAQTab() {
  const navigate = useNavigate();
  const [faqsData, setFaqsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const faqsQuery = query(collection(db, 'faqs'), orderBy('createdAt', 'asc'));
        const querySnapshot = await getDocs(faqsQuery);
        
        const faqs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const grouped = {};
        faqs.forEach(faq => {
          if (!grouped[faq.category]) {
            grouped[faq.category] = [];
          }
          grouped[faq.category].push({
            question: faq.question,
            answer: faq.answer
          });
        });

        const faqCategories = Object.keys(grouped).map(category => ({
          category,
          icon: category === 'About the Master Plan' ? MapPin : 
                category === 'Community Involvement' ? Users :
                category === 'Timeline & Process' ? Calendar : FileText,
          faqs: grouped[category]
        }));

        setFaqsData(faqCategories);
      } catch (err) {
        console.error('Error fetching FAQs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  if (loading) return <div className='text-white text-center py-8'>Loading FAQs...</div>;

  return (
    <div className='space-y-6'>
      <div className='section-card'>
        <div className='flex items-center justify-between'> 
          <div>
            <h2 className='section-title'>Frequently Asked Questions</h2>
            <p className='section-text'>Common questions and answers about the Miner's Park Master Plan</p>
          </div>
          <div className='flex items-center gap-3'>
            <Badge variant='secondary' className='flex items-center gap-1'>
              <HelpCircle className='h-3 w-3' />
              {faqsData.reduce((total, cat) => total + cat.faqs.length, 0)} Questions
            </Badge>
            {user && (
              <Button 
                onClick={() => navigate('/manage-faqs')}
                className='bg-primary-500 hover:bg-primary-600 text-white flex items-center gap-2'
              >
                <Settings className='h-4 w-4' />
                Manage
              </Button>
            )}
          </div>
        </div>
      </div>

      {faqsData.map((category, categoryIndex) => {
        const IconComponent = category.icon;
        return (
          <Card key={categoryIndex}>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <IconComponent className='h-5 w-5 text-primary' />
                {category.category}
              </CardTitle>
              <CardDescription>
                {category.faqs.length} questions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type='single' collapsible className='w-full'>
                {category.faqs.map((faq, faqIndex) => (
                  <AccordionItem
                    key={faqIndex}
                    value={'item-' + categoryIndex + '-' + faqIndex}
                  >
                    <AccordionTrigger className='text-left'>
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className='text-muted-foreground whitespace-pre-wrap'>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        );
      })}

      <Card className='bg-black/60 border-primary/30'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-white'>
            <MessageCircle className='h-5 w-5 text-secondary' />
            Still Have Questions?
          </CardTitle>
          <CardDescription className='text-secondary'>
            We are here to help and want to hear from you
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className='text-sm mb-4 text-white/80'>
            If you could not find the answer you are looking for, please do not
            hesitate to reach out. We value your input and are committed to
            keeping you informed throughout the master plan process.
          </p>
          <Button variant='secondary' className='w-full'>
            <a
              href='/contact'
              className='flex items-center gap-2 text-black font-semibold'
            >
              Contact Us
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
